// import CanoeSdk from "@csc/canoe-sdk";
import {
  AdditionGoodDialog,
  BuyVipDialog,
  openPayRetention,
  VipAuthDialog,
} from "@/components/Vip";
import useAppStore, { refreshVips } from "@/context/store";
import { getCounponByActivity } from "@/services/biz";
import {
  getDeliveryByPosition,
  getDeliveryDetailByModuleId,
} from "@/services/qingzhou";
import { TrackerEvent } from "@/tracker/shared/event";
import { PAGE_CHANGED } from "@/tracker/shared/variable";
import AppEvent, { SimpleEvent } from "@/utils/event";
import { safeParse } from "@/utils/safeParse";
import Cookies from "js-cookie";
import { omit } from "lodash";
import { match } from "path-to-regexp";
import { parseVipUrlHash } from "./gaodingVip";
import { globalDialogControl } from "./globalDialog";
import { getPageEnterRecord } from "./hooks/usePageEnterRecord";
// import { OperationPlugin } from '@csc/canoe-sdk/lib';

type PathPositions = Record<string, string[]>;
type ModuleType =
  | "global_image_dialog"
  | "global_common_dialog"
  | "common_bubble"
  | "NPS"
  | "dialog_web_retain"
  | "recommend_good"
  | "recommend_good_merged"
  | "pay_dialog";

interface TriggerData {
  trigger_condition: string;
  trigger_type: string;
}

interface PitModule {
  end_time: number;
  is_now_pop_up: boolean;
  material_type: string;
  module_id: number;
  module_type: ModuleType;
  pit_mark: string;
  pit_name: string;
  plan_id: string;
  plan_name: string;
  trigger_condition?: string;
  trigger_type?: string;
  triggers: TriggerData[];
}

interface DeliveryMod {
  idea_cnt: number;
  is_dynamic: 0 | 1;
  pit_name: string;
  position: string;
  weight_down_flag: boolean;
  pit_modules: PitModule[];
}

// 页面与位置的对应关系信息
const allPath = "/:path(.*)";
const positions = {
  "/discovery": ["discovery_dialog", "discovery_nps"],
  "/pins/:pinId": [
    "pin_detail_download_bubble",
    "pin_detail_dialog",
    "pin_detail_nps",
  ],
  "/me/right": ["vip_right_dialog"],
  "/search": ["search_dialog", "search_nps"],
  "/materials": ["discovery_material_dialog", "discovery_material_nps"],
  "/follow": ["follow_dialog", "follow_nps"],
  "/vip/info": ["vip_info_dialog"],
  "/activities": ["activities_nps"],
  "/board/:id": ["board_detail_nps"],
  "/user/:user": ["user_page_nps"],
  "/ai": ["ai_home_nps"],
  "/topics/:topicId": ["topic_detail_dialog", "topic_detail_nps"],
  "/pins/:pinId/similar/materials": ["similar_material_nps"],
  "/similar/materials": ["similar_material_nps"],
  [allPath]: ["global-dialog"],
};

const SessionCacheKey = "positionDeliveryCache";
const LocalStorageKey = "HBTriggerStore";

/**
 * 预置条件
 */
export const PRESETS_CONDITION = {
  // 当前页面停留时长
  CUR_PAGE_STAY_SECONDS: "CUR_PAGE_STAY_SECONDS",
  // 当前页面进入次数
  CUR_PAGE_ENTER_TIMES: "CUR_PAGE_ENTER_TIMES",
  // 花瓣素材转采次数
  HUABAN_MATERIAL_COLLECT_TIMES: "HUABAN_MATERIAL_COLLECT_TIMES",
  // 当日剩余下载次数小于N次
  HUABAN_CUR_DOWNLOAD_BALANCE: "HUABAN_CUR_DOWNLOAD_BALANCE",
  // 花瓣会员到期前N天
  HUABAN_VIP_EXPIRE_BEFORE_DAYS: "HUABAN_VIP_EXPIRE_BEFORE_DAYS",
  // 花瓣会员到期后N天
  HUABAN_VIP_EXPIRE_AFTER_DAYS: "HUABAN_VIP_EXPIRE_AFTER_DAYS",
  // 当日第N次进入会员权益页
  HUABAN_VIP_PAGE_ENTER_TIMES: "HUABAN_VIP_PAGE_ENTER_TIMES",
  // 关闭购买弹窗次数
  TODAY_CLOSE_PAY_DIALOG_TIMES: "TODAY_CLOSE_PAY_DIALOG_TIMES",
  // 进入主站的次数
  CUR_PROJECT_ENTER_TIMES: "CUR_PROJECT_ENTER_TIMES",
  // 素材详情页进入次数
  HUABAN_MATERIAL_DETAILS_ENTER_TIMES: "HUABAN_MATERIAL_DETAILS_ENTER_TIMES",
  // 当日进入会员订购页次数（融合）
  MERGE_VIP_PAGE_ENTER_TIMES: "MERGE_VIP_PAGE_ENTER_TIMES",
  // 当日进入相似商用素材页次数
  SIMILAR_MATERIAL_PAGE_ENTER_TIMES: "SIMILAR_MATERIAL_PAGE_ENTER_TIMES",
};

type ActionCallback = (
  getData: () => Promise<any>,
  config: { trigger_type?: string; module_id: number } & Record<string, any>
) => Promise<any>;

/**
 * Steward意为管家，指代这块功能为管理【这块功能太难取名了】
 * 管的事情:
 * 1、页面变化，自动获取当前页面的配置信息
 * 2、监听触发条件, 响应对应的运营策略
 * 3、条件触发次数记录
 * 4、预置条件触发（页面进入次数，停留时长）
 */
class Steward extends SimpleEvent {
  _is_init = false;
  actionCbs: Record<ModuleType, ActionCallback>;
  pageStayTimeouts: NodeJS.Timeout[];
  pageInitPromise: null | Promise<any> = null;

  /**
   * 初始化
   * @param actionCbs 触发条件后响应回调
   */
  constructor(actionCbs: Record<ModuleType, ActionCallback>) {
    super();
    this.actionCbs = actionCbs;
    this.pageStayTimeouts = [];
  }

  init() {
    if (typeof window === "undefined") {
      return;
    }

    if (this._is_init) {
      return;
    }

    this._is_init = true;

    this.pageInitPromise = this.getPagePositionDelivery();

    TrackerEvent.on(PAGE_CHANGED, () => {
      this.pageInitPromise = this.getPagePositionDelivery();
    });
  }

  /**
   * 获取指定位置信息的轻舟运营配置信息
   * 使用场景：不需要在页面访问时请求是否有投放的场景，比如：支付挽留弹窗
   * @param positions 示例： {
   *   "/board/:id": ["board_detail_nps"],
   * }
   * @returns
   */
  async getPositionDelivery(positions: PathPositions) {
    const paths = Object.keys(positions);

    const currentPagePath = paths.find((path) => {
      return match(path)(window.location.pathname);
    });

    if (!currentPagePath || !positions[currentPagePath]) return;

    const delivery = await getDeliveryByPosition(
      positions[currentPagePath].map((p) => ({ position: p }))
    );

    this.initConditionListenr(delivery, currentPagePath);

    this.handlePageStayTimeouts(delivery);
    this.triggerEnterPage();

    return delivery;
  }

  clearPageStayTimeouts() {
    if (this.pageStayTimeouts) {
      this.pageStayTimeouts.forEach((time) => {
        clearTimeout(time);
      });

      this.pageStayTimeouts = [];
    }
  }

  triggerEnterPage() {
    setTimeout(() => {
      this.trigger(PRESETS_CONDITION.CUR_PAGE_ENTER_TIMES);

      // 其他页面进入次数条件触发
      [
        PRESETS_CONDITION.HUABAN_MATERIAL_DETAILS_ENTER_TIMES,
        PRESETS_CONDITION.SIMILAR_MATERIAL_PAGE_ENTER_TIMES,
      ].forEach((type) => {
        this.trigger(type, {
          condition: getPageEnterRecord(type),
        });
      });

      this.trigger(PRESETS_CONDITION.MERGE_VIP_PAGE_ENTER_TIMES, {
        condition: Cookies.get("LOCAL_OPEN_HB_PAGE_KEY"),
      });
    }, 200);
  }

  handlePageStayTimeouts(deliverys: DeliveryMod[]) {
    deliverys.forEach((item) => {
      item.pit_modules.forEach((mod) => {
        const triggers = mod.triggers || [
          {
            trigger_condition: mod.trigger_condition,
            trigger_type: mod.trigger_type,
          },
        ];

        const timeTrigger = triggers.find(
          (item) =>
            item.trigger_type === PRESETS_CONDITION.CUR_PAGE_STAY_SECONDS
        );

        if (timeTrigger) {
          this.pageStayTimeouts.push(
            setTimeout(() => {
              this.trigger(PRESETS_CONDITION.CUR_PAGE_STAY_SECONDS, {
                condition: timeTrigger.trigger_condition,
              });
            }, Number(timeTrigger.trigger_condition) * 1000 + 200)
          );
        }
      });
    });
  }

  /**
   * 获取页面所包含的位置对应的运营配置，即轻舟的投放计划
   */
  async getPagePositionDelivery() {
    const paths = Object.keys(positions);
    const positionDeliveryCache: Record<string, DeliveryMod[]> = safeParse(
      sessionStorage.getItem(SessionCacheKey) || "{}"
    );

    // 取消页面停留时长的条件触发
    this.clearPageStayTimeouts();

    // 获取页面对应的位置信息
    for (let i = 0; i < paths.length; i++) {
      const path = paths[i];
      const result = match(path)(window.location.pathname);

      if (!result) continue;
      // 没有缓存
      if (!positionDeliveryCache[path]) {
        const res = await getDeliveryByPosition(
          (path === allPath
            ? positions[path]
            : [
                ...positions[path as keyof typeof positions],
                ...positions[allPath],
              ]
          ).map((position) => ({
            position,
          }))
        );
        if (res.err) {
          continue;
        }
        positionDeliveryCache[path] = res;
        sessionStorage.setItem(
          SessionCacheKey,
          JSON.stringify(positionDeliveryCache)
        );
      }
      // 页面切换，重置回调
      this.cb = {};
      // 进入页面初始化条件监听
      this.initConditionListenr(positionDeliveryCache[path], path);

      // 触发进入页面条件
      this.triggerEnterPage();

      // 遍历查看是否有条件是 页面停留时长 的配置
      this.handlePageStayTimeouts(positionDeliveryCache[path]);

      break;
    }
  }

  /**
   * 添加触发条件监听，监听条件，触发响应的组件
   * @param {{
   *  idea_cnt: number,
   *  is_dynamic: 0|1,
   *  pit_name: string,
   *  position: string,
   *  weight_down_flag: boolean,
   *  pit_modules: {
   *    end_time: number,
   *    is_now_pop_up: boolean,
   *    material_type: string,
   *    module_id: number,
   *    module_type: string,
   *    pit_mark: string,
   *    pit_name: string,
   *    plan_id: string,
   *    plan_name: string,
   *    trigger_condition: null|string,
   *    trigger_type: null|string,
   *    triggers: {
   *      trigger_condition: string,
   *      trigger_type: string,
   *    }[]
   *  }[]
   * }[]} mods
   * @param {string} pagePath
   */
  initConditionListenr(mods: DeliveryMod[], pagePath: string) {
    if (!mods || mods.length === 0) {
      return;
    }

    mods.forEach((mod) => {
      const pit_modules = mod?.pit_modules;
      pit_modules.forEach((el) => {
        const triggers = el.triggers || [
          {
            trigger_condition: el.trigger_condition,
            trigger_type: el.trigger_type,
          },
        ];

        const isAllTriggered = (module_id: number, triggers: TriggerData[]) => {
          const records = safeParse(localStorage.getItem(LocalStorageKey));
          const record = records?.[module_id];

          if (record?.length > 0) {
            const result = triggers.every((trigger) => {
              return record.some((type: string) => {
                return trigger.trigger_type === type;
              });
            });

            if (result) {
              // 清除已经触发的数据记录
              localStorage.setItem(
                LocalStorageKey,
                JSON.stringify(omit(records, module_id))
              );
            }

            return result;
          }

          return false;
        };
        const addTriggerRecord = (module_id: number, trigger_type: string) => {
          let records = safeParse(localStorage.getItem(LocalStorageKey));
          if (!records) {
            records = {};
          }

          const record = records[module_id] || [];

          if (record.includes(trigger_type)) {
            return;
          }
          record.push(trigger_type);

          records[module_id] = record;
          localStorage.setItem(LocalStorageKey, JSON.stringify(records));
        };

        const mapOnTrigger = (trigger: TriggerData) => {
          const { trigger_type, trigger_condition } = trigger;

          if (!trigger_type) {
            return;
          }

          this.on(trigger_type, async (otherData: any) => {
            let condition;
            if (
              [
                PRESETS_CONDITION.CUR_PAGE_STAY_SECONDS,
                PRESETS_CONDITION.HUABAN_VIP_EXPIRE_BEFORE_DAYS,
                PRESETS_CONDITION.HUABAN_VIP_EXPIRE_AFTER_DAYS,
                PRESETS_CONDITION.HUABAN_CUR_DOWNLOAD_BALANCE,
                PRESETS_CONDITION.HUABAN_MATERIAL_DETAILS_ENTER_TIMES,
                PRESETS_CONDITION.MERGE_VIP_PAGE_ENTER_TIMES,
                PRESETS_CONDITION.SIMILAR_MATERIAL_PAGE_ENTER_TIMES,
              ].includes(trigger_type)
            ) {
              // 这类条件的触发数量从触发者（业务页面代码）传递
              condition = otherData?.condition;
            } else {
              // 其他条件则需要前端计数

              condition = this.addConditionTriggerRecord(
                trigger_type,
                el.module_id,
                pagePath
              );
            }

            // 是否满足条件
            let isMet = +condition === +trigger_condition;

            // 一些条件的是否满足是小于设置值
            if (
              [
                PRESETS_CONDITION.HUABAN_CUR_DOWNLOAD_BALANCE,
                PRESETS_CONDITION.HUABAN_VIP_EXPIRE_BEFORE_DAYS,
                PRESETS_CONDITION.HUABAN_VIP_EXPIRE_AFTER_DAYS,
              ].includes(trigger_type)
            ) {
              isMet = condition <= +trigger_condition;
            }
            // 条件的值是否满足是大于设置值
            if (
              [
                PRESETS_CONDITION.TODAY_CLOSE_PAY_DIALOG_TIMES,
                PRESETS_CONDITION.HUABAN_MATERIAL_DETAILS_ENTER_TIMES,
                PRESETS_CONDITION.MERGE_VIP_PAGE_ENTER_TIMES,
                PRESETS_CONDITION.SIMILAR_MATERIAL_PAGE_ENTER_TIMES,
              ].includes(trigger_type)
            ) {
              isMet = condition >= +trigger_condition;
            }

            if (isMet) {
              addTriggerRecord(el.module_id, trigger_type);
            }

            // 数量要求匹配成功，则进行响应
            if (isAllTriggered(el.module_id, triggers)) {
              const getData = () => {
                return getDeliveryDetailByModuleId(
                  el.pit_mark,
                  el.module_id
                ).then((data) => {
                  if (!data || data.message) {
                    return false;
                  }

                  return data;
                });
              };

              const actionCb =
                this.actionCbs[
                  el.material_type === "null"
                    ? el.module_type
                    : (el.material_type as ModuleType)
                ];

              actionCb?.(getData, {
                otherData,
                // 位置信息
                position: el.pit_mark,
                ...el,
              });
            }
          });
        };

        triggers.forEach(mapOnTrigger);
      });
    });
  }

  /**
   * 记录条件触发次数,并返回累计触发次数
   * @param condition 条件code
   * @param module_id id
   * @param pagePath 页面
   * @returns {number}
   */
  addConditionTriggerRecord(
    condition: undefined | string,
    module_id: number,
    pagePath: string
  ) {
    let key = condition + "_" + module_id;

    if (condition === PRESETS_CONDITION.CUR_PAGE_ENTER_TIMES) {
      key += "_" + pagePath;
    }

    let record;
    record = safeParse(
      window.localStorage.getItem("hb_qz_condition_record") || "{}",
      () => {
        return {};
      }
    );

    if (record[key]) {
      record[key]++;
    } else {
      record[key] = 1;
    }

    window.localStorage.setItem(
      "hb_qz_condition_record",
      JSON.stringify(record)
    );

    return record[key];
  }

  /**
   * 需要等待初始化才能触发
   * @param condition
   * @param otherData
   */
  async trigerAwaitInit(
    condition: string,
    otherData?: { condition?: number } & Record<string, any>
  ) {
    await this.pageInitPromise;
    this.trigger(condition, otherData);
  }
}

/**
 * 移除条件记录的次数
 * @param  trigger_type 条件code
 * @param  module_id id
 */
const removeConditionTriggerRecord = (
  trigger_type?: string,
  module_id?: number
) => {
  let key = trigger_type + "_" + module_id;
  let record = safeParse(
    window.localStorage.getItem("hb_qz_condition_record") || "{}",
    () => {
      return {};
    }
  );
  if (typeof record === "object" && record[key]) {
    delete record[key];
    window.localStorage.setItem(
      "hb_qz_condition_record",
      JSON.stringify(record)
    );
  }
};

// 对应组件响应(相当于回调)
const actionCbs: Record<ModuleType, ActionCallback> = {
  async global_image_dialog(
    getData,
    { module_name, trigger_type, module_id, otherData, plan_id }
  ) {
    if (useAppStore.getState().isMobile) {
      return;
    }

    const getProps = async () => {
      const data = await getData();
      if (otherData?.needRemoveTrigger) {
        removeConditionTriggerRecord(trigger_type as string, module_id);
      }
      if (!data || data.message || data.err) {
        return false;
      }
      return {
        ...data,
        module_name,
        module_id,
        plan_id,
        onOk() {
          dialogActionCb(data);
        },
      };
    };

    // 根据模块名称判断优先级
    const isHightPriority = module_name.includes("（优先级高）");

    const { HBImageDialog } = await import("@/components/HBModal/common");

    globalDialogControl.push({
      dialog: HBImageDialog,
      props: getProps,
      priority: isHightPriority ? 0 : 1,
    });
  },
  async global_common_dialog(
    getData,
    { module_name, otherData, trigger_type, module_id, plan_id }
  ) {
    if (useAppStore.getState().isMobile) {
      return;
    }

    const getProps = async () => {
      const data = await getData();
      if (otherData?.needRemoveTrigger) {
        removeConditionTriggerRecord(trigger_type, module_id);
      }
      if (!data || data.message || data.err) {
        return false;
      }
      return {
        ...data,
        module_name,
        module_id,
        plan_id,
        onOk() {
          dialogActionCb(data.okButtonProps);
        },
        onCancel() {
          dialogActionCb(data.cancelButtonProps);
        },
      };
    };
    // 根据模块名称判断优先级
    const isHightPriority = module_name.includes("（优先级高）");
    const { HBCommonDialog } = await import("@/components/HBModal/common");
    globalDialogControl.push({
      dialog: HBCommonDialog,
      props: getProps,
      priority: isHightPriority ? 0 : 1,
    });
  },
  async common_bubble(getData, { position }) {
    AppEvent.trigger(position, getData);
  },

  async NPS(getData, position) {
    if (useAppStore.getState().isMobile) {
      return;
    }

    const getProps = async () => {
      const data = await getData();
      if (!data || data.message || data.msg) {
        return false;
      }
      return {
        data,
        position,
      };
    };

    const { NPS } = await import("@/components/Nps");
    globalDialogControl.push({
      dialog: NPS,
      props: getProps,
    });
  },
  // 支付挽留弹窗信息 优惠券
  async dialog_web_retain(
    getData,
    {
      otherData: { checkCondition, onOk },
      trigger_type,
      module_id,
      module_name,
    }
  ) {
    removeConditionTriggerRecord(trigger_type, module_id);
    const data = await getData();

    // 如果不反回数据则代表不满足频控限制
    if (!data?.activity_code || data.message) {
      return false;
    }
    // 检测优惠券状态是否满足条件
    let condition = await checkCondition(data.activity_code);
    if (!condition) {
      return false;
    }
    const res = await getCounponByActivity(
      {
        activity_code: data.activity_code,
      },
      true
    );
    // 失败处理，失败会直接返回字符串告知失败原因
    if (typeof res === "string" || res instanceof Error || res.err) {
      return false;
    }
    // 只要有一张领取成功则展示弹窗
    const isSucceed = res?.some((item) => item.is_success);
    if (res.length && isSucceed) {
      const { default: CounponModal } = await import(
        "@/components/CounponModal"
      );
      CounponModal.open({
        imgUrl: data.banner_img_url,
        module_name,
        module_id,
        // 倒计时只取第一张券时间
        duration: res[0].coupon?.valid_time?.relative_time_duration,
        onOk() {
          onOk();
        },
      });
    }
  },

  // 推荐商品 带支付二维码
  async recommend_good(getData, { trigger_type, module_id }) {
    removeConditionTriggerRecord(trigger_type, module_id);
    const data = await getData();

    const { PayRetentionModal } = await import("@/components/Vip/payRetention");

    PayRetentionModal.open({
      config: data,
      onOk() {
        // 更新用户会员信息
        refreshVips();
        useAppStore.getState().refreshUser();
      },
    });
  },
  /**
   * 融合用户推荐商品 带支付二维码(支付挽留)
   */
  async recommend_good_merged(getData, { trigger_type, module_id }) {
    removeConditionTriggerRecord(trigger_type, module_id);
    const data = await getData();

    openPayRetention({
      params: data,
      onOk() {
        // 更新用户会员信息
        refreshVips();
        useAppStore.getState().refreshUser();
      },
    });
  },

  async pay_dialog(getData, { trigger_type, module_id, otherData }) {
    const getProps = async () => {
      const data = await getData();
      if (otherData?.needRemoveTrigger) {
        removeConditionTriggerRecord(trigger_type, module_id);
      }
      if (!data || data.message || data.msg) {
        return false;
      }
      return {
        data,
        "data-source-location": "资源位触发",
      };
    };

    globalDialogControl.push({
      dialog: BuyVipDialog,
      props: getProps,
    });
  },
};

export const stewardInstance = new Steward(actionCbs);

function dialogActionCb(props: {
  actionType: number;
  href: string;
  goodType: string;
  actionGood: string;
}) {
  switch (props.actionType) {
    // 链接跳转
    case 0:
      window.open(props.href);
      break;
    // 购买弹框
    case 2:
      const { goodType, actionGood } = props;
      const actionsMap = {
        buyVip: BuyVipDialog.open,
        buyVipAuthExtend: VipAuthDialog.open,
        buyAddition: AdditionGoodDialog.open,
      };

      if (actionsMap[goodType as keyof typeof actionsMap]) {
        if (goodType === "buyVip") {
          const {
            type: vipType,
            selectedPkg,
            selectedPlusSku,
            versionTab,
            selectSkuId,
            buyType,
          } = parseVipUrlHash("buyVip-" + actionGood);

          return BuyVipDialog.open({
            type: vipType,
            selectedPkg: (selectedPkg || "").split(";"),
            defaultSelectPlusSku: selectedPlusSku,
            /**企业会员选中Version（基础授权、高级授权）使用 */
            versionTab,
            selectSkuId,
            buyType,
          });
        }

        actionsMap[goodType as keyof typeof actionsMap]({
          selectedPkg: (actionGood || "").split("-"),
        });
      }
      break;
    default:
      break;
  }
}
